import { AdCard } from './AdCard'
import { useState } from 'react'
import { creativeLetter, getCreativesFromGroup } from 'app/util'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import BrokenImageIcon from '@mui/icons-material/BrokenImage'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { useHistory } from 'react-router-dom'
import { useGetBusinessID } from '@marketing-milk/frontend'
import { AdCreativeGroup } from 'pages/business/campaigns/forms/CampaignForm'
import { FBAdPreviewFormat } from '@marketing-milk/interfaces'
import { getPreview } from 'pages/business/creatives/creative-wizard/ad-preview/views/getPreview'
import { Drawer, Button, Box } from '@mui/material'
import { useFetchCreativePreview } from 'hooks/creatives/fetch-creative-preview'

export interface AdSummaryProps {
  selectedCreatives?: AdCreativeGroup[]
  sprintId?: number
  fromTab?: 'creatives' | 'draft-campaigns' | 'published-campaigns'
}

export function AdSummary({ selectedCreatives, sprintId, fromTab }: AdSummaryProps) {
  const [activeCreativesIndex, setActiveCreativesIndex] = useState(0)
  const [viewPreview, setViewPreview] = useState(false)
  const [previewCreativeId, setPreviewCreativeId] = useState<number | undefined>()
  const history = useHistory()
  const businessID = useGetBusinessID()
  const activeCreatives = getCreativesFromGroup(activeCreativesIndex, selectedCreatives).filter(
    (c): c is NonNullable<typeof c> => c !== null
  )
  const currentAdCreativeGroup = activeCreativesIndex + 1
  const previousAdCreativeGroup = () => setActiveCreativesIndex(activeCreativesIndex - 1)
  const nextAdCreativeGroup = () => setActiveCreativesIndex(activeCreativesIndex + 1)

  const { adCreativePreview, retrievePreview } = useFetchCreativePreview(previewCreativeId)

  const handleEditCreative = (id: number) => {
    const baseRoute = sprintId ? 'sprints' : 'creatives'
    const params = new URLSearchParams()
    if (sprintId) params.append('sprintId', sprintId.toString())
    if (fromTab) params.append('tab', fromTab)
    const queryString = params.toString() ? `?${params.toString()}` : ''
    history.push(`/${businessID}/${baseRoute}/${id}${queryString}`)
  }

  const handlePreviewCreative = (id: number) => {
    setPreviewCreativeId(id)
    setViewPreview(true)
    retrievePreview(id)
  }

  const getCardActions = (id: number) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<VisibilityIcon />}
        onClick={() => handlePreviewCreative(id)}
        size="small"
      >
        Preview
      </Button>
      <Button
        variant="contained"
        color="primary"
        startIcon={<EditIcon />}
        onClick={() => handleEditCreative(id)}
        size="small"
      >
        Edit
      </Button>
    </Box>
  )

  const noCreative = (
    <div className="h-100 d-flex justify-content-center">
      <div className="center-vertical text-center">
        <BrokenImageIcon
          color="disabled"
          className="mx-auto mb-2"
          fontSize="large"
          style={{ fontSize: '40px' }}
        />
        <Typography variant="h5" className="MuiTypography-colorTextSecondary">
          No Creative Selected
        </Typography>
      </div>
    </div>
  )

  return (
    <div data-testid="ad-summary" className="w-100">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {activeCreatives.length > 0
          ? activeCreatives.map((creative, i) => (
              <div key={creative.id}>
                <AdCard
                  title={`Creative ${creativeLetter(i)}`}
                  creative={creative}
                  actions={getCardActions(creative.id)}
                />
              </div>
            ))
          : noCreative}
      </div>
      {selectedCreatives && selectedCreatives?.length > 1 && (
        <div data-testid="ad-group-controls" className="w-100 mt-4">
          <div className="d-flex fit-content mx-auto text-center">
            <IconButton
              data-testid="previous-ad-group"
              size="medium"
              disabled={activeCreativesIndex === 0}
              onClick={previousAdCreativeGroup}
            >
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            <Typography className="mx-4 center-vertical" variant="subtitle1">
              {currentAdCreativeGroup} of {selectedCreatives.length}
            </Typography>
            <IconButton
              data-testid="next-ad-group"
              size="medium"
              disabled={currentAdCreativeGroup === selectedCreatives.length}
              onClick={nextAdCreativeGroup}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}

      <Drawer
        open={viewPreview}
        onClose={() => setViewPreview(false)}
        anchor="right"
        PaperProps={{
          style: { width: '500px' },
        }}
        ModalProps={{
          BackdropProps: {
            style: { backgroundColor: 'rgba(0, 0, 0, 0.2)' },
          },
        }}
      >
        <div className="flex flex-col p-4 h-full overflow-y-auto space-y-4">
          <div className="flex justify-between items-center border-b pb-3">
            <p className="font-extrabold text-xl">Ad Preview</p>
            <i
              className="fa fa-times cursor-pointer text-gray-600 hover:text-gray-900"
              onClick={() => setViewPreview(false)}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {adCreativePreview && getPreview(FBAdPreviewFormat.MOBILE_FULLWIDTH, adCreativePreview)}
          </div>
        </div>
      </Drawer>
    </div>
  )
}
